import { useFragment } from "@/generated";
import {
  type SlideFieldsFragment,
  SlideFieldsFragmentDoc,
  SlidesByLessonIdDocument,
} from "@/generated/graphql";
import {
  type SlidesByLessonIdOptions,
  getSlidesByLessonIdVars,
} from "@/lib/utils/graphql/getSlidesByLessonIdVars";
import { useQuery } from "@apollo/client/react";
import { useEffect, useRef } from "react";

/** A main slide with its subslides, which the lesson list never returns on their own. */
export type SlideWithSubslides = SlideFieldsFragment & { children: SlideFieldsFragment[] };

/**
 * Fetches EVERY slide of a lesson, pulling the remaining pages itself as each one lands, so callers
 * never drive pagination — `lessonSlidesHasMore`/`handleLoadMoreLessonSlides` are only for showing
 * progress or retrying by hand. A revisit renders the cached list at once and re-reads it behind
 * that, so slides a generation job finished while the reader was away still turn up.
 */
export function useSlidesByLessonId({
  searchText,
  lessonId,
  sortBy,
  isTrash = false,
  skip = false,
  slideType,
  domain,
  knowledgeArea,
  isHidden,
  isCompleted,
}: SlidesByLessonIdOptions) {
  const variables = getSlidesByLessonIdVars({
    lessonId,
    searchText,
    sortBy,
    isTrash,
    slideType,
    domain,
    knowledgeArea,
    isHidden,
    isCompleted,
  });

  const {
    data: lessonSlidesData,
    loading: lessonSlidesLoading,
    fetchMore: fetchMoreLessonSlides,
    error: lessonSlidesError,
    refetch: refetchLessonSlides,
  } = useQuery(SlidesByLessonIdDocument, {
    variables,
    fetchPolicy: "cache-and-network",
    skip,
  });

  const lessonSlideItems: SlideWithSubslides[] = (lessonSlidesData?.slidesByLessonId?.items || [])
    .map((item) => ({
      ...useFragment(SlideFieldsFragmentDoc, item),
      children: item.children
        .map((child) => useFragment(SlideFieldsFragmentDoc, child))
        // Sorted client-side too, so a reorder inside a group shows up before the next fetch.
        .sort((a, b) => a.sortOrder - b.sortOrder || a.id.localeCompare(b.id)),
    }))
    // Ids break the rare `sortOrder` tie, so b2b-mobile lists the same slides identically.
    .sort((a, b) => a.sortOrder - b.sortOrder || a.id.localeCompare(b.id));
  const lessonSlidesHasMore = lessonSlidesData?.slidesByLessonId?.hasMore || false;

  const loadedCount = lessonSlideItems.length;

  const fetchNextPage = () =>
    fetchMoreLessonSlides({
      variables: { ...variables, offset: loadedCount },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev;

        const existingIds = new Set(
          useFragment(SlideFieldsFragmentDoc, prev.slidesByLessonId.items).map((s) => s.id)
        );
        const newItems = fetchMoreResult.slidesByLessonId.items.filter(
          (item) => !existingIds.has(useFragment(SlideFieldsFragmentDoc, item).id)
        );

        return {
          slidesByLessonId: {
            ...fetchMoreResult.slidesByLessonId,
            items: [...prev.slidesByLessonId.items, ...newItems],
          },
        };
      },
    });

  // Held in a ref because it closes over the current page count and so changes every render —
  // depending on it directly would re-run this effect forever.
  const fetchNextPageRef = useRef(fetchNextPage);
  fetchNextPageRef.current = fetchNextPage;
  const draining = useRef(false);

  // Pulls the remaining pages the moment one lands, so a caller ends up holding the whole lesson
  // without driving pagination itself.
  //
  // An effect rather than a completion callback: a page also arrives from the CACHE on a revisit,
  // and nothing fires then — a drain interrupted by navigating away would stay half-finished for
  // as long as that cache entry lived.
  //
  // Self-terminating in every direction. A page that fails, or that comes back empty while still
  // claiming `hasMore`, leaves `loadedCount` untouched, so these dependencies don't change and the
  // drain stops instead of retrying in a loop; `refetchLessonSlides` is the way back from that.
  // biome-ignore lint/correctness/useExhaustiveDependencies: fetchNextPage is held in a ref on purpose — see above
  useEffect(() => {
    if (!lessonSlidesHasMore || draining.current) return;

    draining.current = true;
    fetchNextPageRef
      .current()
      .catch(() => {
        // Non-fatal: whatever already loaded stays on screen rather than the list emptying.
      })
      .finally(() => {
        draining.current = false;
      });
  }, [lessonSlidesHasMore, loadedCount]);

  return {
    lessonSlideItems,
    lessonSlidesHasMore,
    lessonSlidesLoading,
    lessonSlidesError,
    refetchLessonSlides,
    handleLoadMoreLessonSlides: fetchNextPage,
  };
}
